import { ArrowRight, RefreshCcw } from 'lucide-react';
import { Link } from 'react-router-dom';
import { StatusBadge } from './StatusBadge';
import { useRestructureChain } from '@/hooks/use-loans';
import type { LoanStatus } from '@/types';

interface Props {
  loanId: string;
}

function fmt(num: number): string {
  return new Intl.NumberFormat('en-AU', {
    style: 'currency',
    currency: 'AUD',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(num);
}

export function RestructureChainBanner({ loanId }: Props) {
  const { data: chain } = useRestructureChain(loanId);

  // Only show if there's a chain (more than just the current loan)
  if (!Array.isArray(chain) || chain.length <= 1) return null;

  return (
    <div className="bg-amber-50 border border-amber-200 rounded-lg p-4">
      <div className="flex items-center gap-2 mb-3">
        <RefreshCcw className="w-4 h-4 text-amber-600" />
        <h3 className="text-sm font-semibold text-amber-800">Restructure History</h3>
        <span className="text-xs text-amber-600 bg-amber-100 px-1.5 py-0.5 rounded-full">
          {chain.length} loans
        </span>
      </div>

      <div className="flex items-center gap-2 flex-wrap">
        {chain.map((item, i) => (
          <div key={item.id} className="flex items-center gap-2">
            {i > 0 && <ArrowRight className="w-3.5 h-3.5 text-amber-400 shrink-0" />}
            <div
              className={`rounded-md px-3 py-2 border text-sm ${
                item.direction === 'current'
                  ? 'bg-white border-amber-300 shadow-sm'
                  : 'bg-amber-50/50 border-amber-200'
              }`}
            >
              {item.direction === 'current' ? (
                <div>
                  <span className="font-mono font-semibold text-amber-800">{item.loanNumber}</span>
                  <div className="text-xs text-amber-600 mt-0.5">
                    {fmt(item.principal)} · {item.interestRate.toFixed(2)}% · {item.termMonths}mo
                  </div>
                </div>
              ) : (
                <Link to={`/loans/${item.id}`} className="hover:underline">
                  <span className="font-mono font-medium text-amber-700">{item.loanNumber}</span>
                  <div className="flex items-center gap-2 mt-0.5">
                    <span className="text-xs text-amber-500">
                      {fmt(item.principal)} · {item.interestRate.toFixed(2)}%
                    </span>
                    <StatusBadge status={item.status as LoanStatus} />
                  </div>
                </Link>
              )}
            </div>
          </div>
        ))}
      </div>

      {chain.find(c => c.direction === 'current')?.restructureReason && (
        <p className="mt-2 text-xs text-amber-600 italic">
          Reason: {chain.find(c => c.direction === 'current')?.restructureReason}
        </p>
      )}
    </div>
  );
}
